/**
 * AdminAssetCreatePage — Issue #364.
 *
 * Lets administrators register a new fixed asset. Money fields are decimal-safe
 * strings; the backend rejects negative / non-decimal / >4-fractional-digit
 * inputs with 400 responses surfaced through useAdminAssetMutations.
 *
 * 红线 (AI GUIDE §6 / §7):
 *   - 不接受 JS number；表单收 string 后直接传给 generated client，由后端 Decimal 校验。
 *   - 受限角色未通过 admin_assets:write 的不渲染表单 — 直接 AccessDenied。
 *   - 提交失败后保留输入，错误以 Card 提示，不主动覆盖用户字段。
 */
import { useState } from 'react';
import { useLocation } from 'wouter';
import AccessDenied from '@/components/AccessDenied';
import { Card } from '@/components/ui/card';
import { useAuth } from '@/hooks/useAuth';
import {
  ADMIN_ASSETS_PERMISSIONS,
  hasPermission,
} from '@/lib/adminAssetsPermissions';
import {
  ADMIN_ASSET_CATEGORY_LABELS,
  ADMIN_ASSET_DEPRECIATION_METHOD_LABELS,
  useAdminAssetMutations,
  type AdminAssetDto,
} from '@/hooks/useAdminAssets';

export default function AdminAssetCreatePage() {
  const { permissions } = useAuth();
  const canWrite = hasPermission(
    permissions,
    ADMIN_ASSETS_PERMISSIONS.WRITE,
  );
  const [, setLocation] = useLocation();

  const { createAsset, loading, error } = useAdminAssetMutations();

  const [assetCode, setAssetCode] = useState('');
  const [name, setName] = useState('');
  const [category, setCategory] = useState<AdminAssetDto['category']>('computer');
  const [brand, setBrand] = useState('');
  const [modelName, setModelName] = useState('');
  const [serialNo, setSerialNo] = useState('');
  const [purchaseDate, setPurchaseDate] = useState('');
  const [originalCost, setOriginalCost] = useState('');
  const [depreciationMethod, setDepreciationMethod] = useState<
    AdminAssetDto['depreciationMethod']
  >('none');
  const [usefulLifeMonths, setUsefulLifeMonths] = useState('');
  const [residualValue, setResidualValue] = useState('');
  const [location, setLocationField] = useState('');
  const [notes, setNotes] = useState('');

  if (!canWrite) return <AccessDenied resource="行政固定资产" />;

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const body = {
      assetCode: assetCode.trim(),
      name: name.trim(),
      category,
      brand: brand.trim() || undefined,
      model: modelName.trim() || undefined,
      serialNo: serialNo.trim() || undefined,
      purchaseDate: purchaseDate ? new Date(purchaseDate).toISOString() : undefined,
      originalCost: originalCost.trim() || undefined,
      depreciationMethod,
      usefulLifeMonths: usefulLifeMonths ? Number(usefulLifeMonths) : undefined,
      residualValue: residualValue.trim() || undefined,
      location: location.trim() || undefined,
      notes: notes.trim() || undefined,
    };
    const created = await createAsset(body);
    if (created) {
      setLocation(`/admin/assets/${created.id}`);
    }
  };

  return (
    <div className="space-y-4 p-6">
      <header>
        <h1 className="text-xl font-semibold">登记行政资产</h1>
        <p className="text-sm text-slate-500">
          金额字段使用小数字符串，禁止科学计数或带千分位。后端按 DECIMAL(19,4) 校验。
        </p>
      </header>

      {error ? (
        <Card className="border-red-200 bg-red-50 p-4 text-sm text-red-800">
          {error.message}
        </Card>
      ) : null}

      <Card className="p-4">
        <form onSubmit={onSubmit} className="grid grid-cols-2 gap-4 text-sm">
          <label className="col-span-1 flex flex-col gap-1">
            <span>资产编码 *</span>
            <input
              type="text"
              required
              className="rounded border px-2 py-1"
              value={assetCode}
              maxLength={80}
              onChange={(e) => setAssetCode(e.target.value)}
              data-testid="admin-assets-create-code"
            />
          </label>
          <label className="col-span-1 flex flex-col gap-1">
            <span>名称 *</span>
            <input
              type="text"
              required
              className="rounded border px-2 py-1"
              value={name}
              maxLength={200}
              onChange={(e) => setName(e.target.value)}
              data-testid="admin-assets-create-name"
            />
          </label>
          <label className="col-span-1 flex flex-col gap-1">
            <span>类别 *</span>
            <select
              className="rounded border px-2 py-1"
              value={category}
              onChange={(e) =>
                setCategory(e.target.value as AdminAssetDto['category'])
              }
              data-testid="admin-assets-create-category"
            >
              {(
                Object.entries(ADMIN_ASSET_CATEGORY_LABELS) as [
                  AdminAssetDto['category'],
                  string,
                ][]
              ).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <label className="col-span-1 flex flex-col gap-1">
            <span>品牌</span>
            <input
              type="text"
              className="rounded border px-2 py-1"
              value={brand}
              maxLength={120}
              onChange={(e) => setBrand(e.target.value)}
            />
          </label>
          <label className="col-span-1 flex flex-col gap-1">
            <span>型号</span>
            <input
              type="text"
              className="rounded border px-2 py-1"
              value={modelName}
              maxLength={120}
              onChange={(e) => setModelName(e.target.value)}
            />
          </label>
          <label className="col-span-1 flex flex-col gap-1">
            <span>SN / 序列号</span>
            <input
              type="text"
              className="rounded border px-2 py-1"
              value={serialNo}
              maxLength={120}
              onChange={(e) => setSerialNo(e.target.value)}
            />
          </label>
          <label className="col-span-1 flex flex-col gap-1">
            <span>采购日期</span>
            <input
              type="date"
              className="rounded border px-2 py-1"
              value={purchaseDate}
              onChange={(e) => setPurchaseDate(e.target.value)}
            />
          </label>
          <label className="col-span-1 flex flex-col gap-1">
            <span>原值 (decimal string)</span>
            <input
              type="text"
              className="rounded border px-2 py-1 font-mono"
              value={originalCost}
              placeholder="0.0000"
              onChange={(e) => setOriginalCost(e.target.value)}
              data-testid="admin-assets-create-cost"
            />
          </label>
          <label className="col-span-1 flex flex-col gap-1">
            <span>折旧方式</span>
            <select
              className="rounded border px-2 py-1"
              value={depreciationMethod}
              onChange={(e) =>
                setDepreciationMethod(
                  e.target.value as AdminAssetDto['depreciationMethod'],
                )
              }
            >
              {(
                Object.entries(ADMIN_ASSET_DEPRECIATION_METHOD_LABELS) as [
                  AdminAssetDto['depreciationMethod'],
                  string,
                ][]
              ).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <label className="col-span-1 flex flex-col gap-1">
            <span>折旧年限 (月)</span>
            <input
              type="number"
              min={0}
              className="rounded border px-2 py-1"
              value={usefulLifeMonths}
              onChange={(e) => setUsefulLifeMonths(e.target.value)}
            />
          </label>
          <label className="col-span-1 flex flex-col gap-1">
            <span>残值 (decimal string)</span>
            <input
              type="text"
              className="rounded border px-2 py-1 font-mono"
              value={residualValue}
              placeholder="0.0000"
              onChange={(e) => setResidualValue(e.target.value)}
            />
          </label>
          <label className="col-span-1 flex flex-col gap-1">
            <span>存放地点</span>
            <input
              type="text"
              className="rounded border px-2 py-1"
              value={location}
              maxLength={200}
              onChange={(e) => setLocationField(e.target.value)}
            />
          </label>
          <label className="col-span-2 flex flex-col gap-1">
            <span>备注 (已自动脱敏，不要写入手机号 / 薪资 / token)</span>
            <textarea
              className="rounded border px-2 py-1"
              value={notes}
              maxLength={2000}
              rows={3}
              onChange={(e) => setNotes(e.target.value)}
            />
          </label>

          <div className="col-span-2 flex gap-2">
            <button
              type="submit"
              className="rounded border bg-slate-900 px-4 py-2 text-sm text-white hover:bg-slate-800 disabled:opacity-50"
              disabled={loading}
              data-testid="admin-assets-create-submit"
            >
              {loading ? '提交中…' : '提交登记'}
            </button>
            <button
              type="button"
              className="rounded border px-4 py-2 text-sm hover:bg-muted"
              onClick={() => setLocation('/admin/assets')}
            >
              返回列表
            </button>
          </div>
        </form>
      </Card>
    </div>
  );
}
